/**
 * AppShell — responsive chat-first layout.
 * Mobile: TopBar + content + overlay NavDrawer.
 * Desktop: persistent NavRail + elevated content surface.
 */
import * as React from 'react';
import { NavDrawer, NavRail } from './NavDrawer';
import { TopBar } from './TopBar';
import { CaptureSheet } from '@/components/capture/CaptureSheet';
import { InstallPrompt } from '@/components/shared/InstallPrompt';
import { NotificationBanner } from '@/components/shared/NotificationBanner';
import { useUIStore } from '@/store/uiStore';
import { useChatStore } from '@/store/chatStore';
import { useNotificationPolling } from '@/hooks/useNotificationPolling';
import { WakeWordController } from './WakeWordController';

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { isModalOpen, modalType, closeModal, openModal } = useUIStore();
  const loadSessions = useChatStore((s) => s.loadSessions);
  useNotificationPolling();

  // Load chat sessions once at the shell level so the sidebar's "Chats" list is
  // populated on every page, not only inside the chat view.
  React.useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Handle ?capture=1 shortcut (PWA manifest shortcut)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('capture') === '1') {
      openModal('capture');
      params.delete('capture');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="h-dvh overflow-hidden surface-base">
      <a href="#main-content" className="skip-to-content">Skip to content</a>
      <NotificationBanner />
      <WakeWordController />

      <div className="flex h-full">
        {/* Desktop rail */}
        <NavRail />

        {/* Content column */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <TopBar />
          <main id="main-content" className="flex min-h-0 flex-1 flex-col" role="main">
            {children}
          </main>
        </div>
      </div>

      {/* Mobile overlay drawer (no-op visual on desktop; rail is used there) */}
      <NavDrawer />

      <CaptureSheet
        open={isModalOpen && modalType === 'capture'}
        onOpenChange={(open) => { if (!open) closeModal(); }}
      />

      <InstallPrompt />
    </div>
  );
}
